import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import Select from 'react-select';
import { UserUIModel } from '../../../../../shared/ui-models/user.ui-model';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getUserByPhone } from '../../../../../shared/services/data/user.data';
import { getModuleName } from '../../../../shared/services/reservation.service';
import {
  CHIP,
  FIELD,
  LABEL,
  LINK_BUTTON,
  OUTLINE_BUTTON,
  SECTION_TITLE,
  selectProps
} from '../../reservation-create.ui';

export interface GuestDetails {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
}

export interface GuestSectionProps {
  data: {
    guest?: GuestDetails;
  };
  eventHandlers: {
    onGuestChange: (guest?: GuestDetails) => void;
  };
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

const PHONE_INPUT_PROPS = {
  containerClass: '!block !w-full !h-[40px]',
  inputClass: `${FIELD} !h-[40px] !w-full !py-0 !pl-[48px] !leading-[40px] !rounded-[10px] !border !border-gray-200 hover:!border-neutral-500 focus:!border-neutral-500 !shadow-none !text-[15px] !font-gilroy-regular !font-normal !text-neutral-500`,
  buttonClass: '!border-0 !bg-transparent',
  specialLabel: ''
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isGuestComplete = (guest?: GuestDetails) =>
  !!guest &&
  (!!guest.id || (!!guest.name.trim() && !!guest.phoneNumber && EMAIL_PATTERN.test(guest.email)));

const toGuest = (user: UserUIModel): GuestDetails => ({
  id: user.id || '',
  name: user.name || '',
  phoneNumber: user.phone || '',
  email: user.email || '',
  gender: user.gender || 'male'
});

export const GuestSection: React.FC<GuestSectionProps> = ({
  data: { guest },
  eventHandlers: { onGuestChange }
}) => {
  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<UserUIModel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const search = () => {
    if (!phone) return;
    setIsSearching(true);
    setNotFound(false);
    setMatches([]);
    getUserByPhone(phone, getModuleName(), { returnAll: true })
      .then((response) => {
        if (isErrorInfo(response)) {
          setNotFound(true);
          return;
        }
        const users = Array.isArray(response) ? response : [response];
        if (users.length === 1) onGuestChange(toGuest(users[0]));
        else setMatches(users);
      })
      .finally(() => setIsSearching(false));
  };

  const startNewGuest = () =>
    onGuestChange({ id: '', name: '', phoneNumber: phone, email: '', gender: 'male' });

  const reset = () => {
    setMatches([]);
    setNotFound(false);
    onGuestChange(undefined);
  };

  if (guest?.id) {
    return (
      <>
        <h2 className={SECTION_TITLE}>Guest</h2>
        <div className="max-w-[720px] flex items-center justify-between gap-[16px] rounded-[12px] border border-gray-200 px-[16px] py-[14px]">
          <div className="flex flex-col gap-[2px] min-w-0">
            <span className="text-[15px] font-gilroy-medium truncate">{guest.name}</span>
            <span className="text-[13px] text-neutral-300 truncate">
              {guest.phoneNumber}
              {guest.email && (
                <>
                  {' '}
                  <span className="text-neutral-300">·</span> {guest.email}
                </>
              )}
            </span>
          </div>
          <button type="button" className={LINK_BUTTON} onClick={reset}>
            Change
          </button>
        </div>
      </>
    );
  }

  if (guest) {
    const update = (patch: Partial<GuestDetails>) => onGuestChange({ ...guest, ...patch });
    const showEmailError = !!guest.email && !EMAIL_PATTERN.test(guest.email);
    return (
      <>
        <h2 className={SECTION_TITLE}>Guest</h2>
        <div className="max-w-[720px] flex flex-col gap-[16px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[12px]">
            <label className={LABEL}>
              Full name
              <input
                className={FIELD}
                value={guest.name}
                onChange={(event) => update({ name: event.target.value })}
              />
            </label>
            <label className={LABEL}>
              Phone
              <PhoneInput
                country="eg"
                value={guest.phoneNumber}
                onChange={(value) => update({ phoneNumber: value ? `+${value}` : '' })}
                {...PHONE_INPUT_PROPS}
              />
            </label>
            <label className={LABEL}>
              Email
              <input
                className={`${FIELD} ${showEmailError ? '!border-scarlet' : ''}`}
                type="email"
                value={guest.email}
                onChange={(event) => update({ email: event.target.value.trim() })}
              />
            </label>
            <label className={LABEL}>
              Gender
              <Select
                {...selectProps}
                options={GENDER_OPTIONS}
                value={GENDER_OPTIONS.find((option) => option.value === guest.gender) || null}
                onChange={(value) => update({ gender: value?.value || 'male' })}
                isSearchable={false}
              />
            </label>
          </div>
          <div>
            <button type="button" className={LINK_BUTTON} onClick={reset}>
              Search existing guests instead
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className={SECTION_TITLE}>Guest</h2>
      <div className="max-w-[720px] flex flex-col gap-[12px]">
        <div className="flex items-end gap-[8px]">
          <label className={`${LABEL} flex-1`}>
            Phone number
            <PhoneInput
              country="eg"
              value={phone}
              onChange={(value) => setPhone(value ? `+${value}` : '')}
              onKeyDown={(event) => event.key === 'Enter' && search()}
              {...PHONE_INPUT_PROPS}
            />
          </label>
          <button
            type="button"
            className={OUTLINE_BUTTON}
            disabled={!phone || isSearching}
            onClick={search}
          >
            {isSearching ? 'Searching…' : 'Search'}
          </button>
        </div>

        {matches.length > 1 && (
          <div className="rounded-[12px] border border-gray-200 divide-y divide-gray-200 overflow-hidden">
            {matches.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => onGuestChange(toGuest(user))}
                className="w-full flex items-center justify-between gap-[12px] px-[16px] py-[12px] bg-neutral-0 hover:bg-creams-100 text-left cursor-pointer"
              >
                <span className="flex flex-col gap-[2px] min-w-0">
                  <span className="text-[14px] font-gilroy-medium truncate">
                    {user.name || 'Unnamed guest'}
                  </span>
                  <span className="text-[13px] text-neutral-300 truncate">
                    {[user.phone, user.email].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className={`${CHIP} bg-creams-100 border border-gray-200 text-neutral-500`}>
                  Select
                </span>
              </button>
            ))}
          </div>
        )}

        {notFound && (
          <div className="text-[14px] text-neutral-300">No guest found with this number</div>
        )}

        <div>
          <button type="button" className={LINK_BUTTON} onClick={startNewGuest}>
            Add new guest
          </button>
        </div>
      </div>
    </>
  );
};
